import {
    EmployeeState,
    Employee,
    EmployeeListResult,
    EnrollmentStatus,
    EmployeeListSortParameters,
    EmployeeSearchDropdownItem,
    FileUploadRequest,
    EmployeeListSearchFields
} from "@/employer/types";
import {
    EmployeeListParams,
    FetchState,
    SortOrder,
    EmploymentStatus,
    EmployeeListSortOption
} from "@/shared/types";
import { FeatureFlags } from "@/shared/flagging";
import * as UserService from "@/shared/api/userService";
import moment from "moment";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import { usePageSizeStore } from "@/shared/store/pageSize";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useNotifierStore } from "@/shared/store/notifier";
import { useUserStore } from "@/shared/store/user";

const generateEmptyState = () => ({
    pageNumber: 1,
    totalRecords: 0,
    sortOrder: SortOrder.Ascending,
    sortOption: EmployeeListSortOption.Name,
    getFetchState: FetchState.Uninitialized,
    pages: [],
    searchDropdownList: []
});

function findEmployee (state: EmployeeState, employeeId: number) {
    for (const page of state.pages) {
        if (!page) {
            continue;
        }

        const employee = page.find(e => e.employeeId === employeeId);

        if (employee) {
            return employee;
        }
    }

    return null;
}

export const useEmployeeStore = defineStore("employee", {
    state: () => (_.cloneDeep(generateEmptyState())),
    getters: {
        getEmployeesByPageNumber: state => (pageNumber: number): Employee[] => {
            const pageObject = state.pages[pageNumber];

            if (typeof pageObject === "undefined") {
                return [];
            }
            return pageObject;
        },
        pageCount () {
            return this.totalRecords < this.pageSize ? 1 : Math.floor(
                (this.totalRecords + this.pageSize - 1) / this.pageSize);
        },
        pageSize () {
            const pageSizeStore = usePageSizeStore();
            return pageSizeStore.pageSize;
        },
        defaultSortOption () {
            const featureFlagStore = useFeatureFlagStore();
            const flags: FeatureFlags = featureFlagStore.flags;
            return flags.bend15691OrderByRelevance
                ? EmployeeListSortOption.None
                : EmployeeListSortOption.Name;
        },
        getEmployeeById () {
          return (employeeId: number) => findEmployee(this, employeeId);
        },
        historicalEligibilityIntervals () {
            return (employeeId: number) => {
                const now = moment();
                const employee = this.getEmployeeById(employeeId);

                if (!employee) {
                    return [];
                }

                if (!employee.hsaIntervals || !employee.hsaIntervals.length) {
                    return [];
                }

                return employee.hsaIntervals.filter(interval => now.isAfter(interval.endDate));
            };
        },
        enrollmentStatusText: state => (employee: Employee) => {
            if (!employee.enrollmentStatus) {
                return "";
            }

            switch (employee.enrollmentStatus) {
                case EnrollmentStatus.None:
                case EnrollmentStatus.Enrolled:
                    return "Enrolled";
                case EnrollmentStatus.Ineligible:
                    return "Ineligible";
                case EnrollmentStatus.InProgress:
                    return "In Progress";
                case EnrollmentStatus.NotStarted:
                    return "Not Started";
                case EnrollmentStatus.AccountClosed:
                    return "Account Closed";
                case EnrollmentStatus.Blocked:
                    return "Blocked";
                default:
                    return "";
            }
        },
        employmentStatusText: state => (employee: Employee) => {
            if (!employee.employmentStatus) {
                return "";
            }
            switch (employee.employmentStatus) {
                case EmploymentStatus.None:
                case EmploymentStatus.New:
                    return "New";
                case EmploymentStatus.Active:
                    return "Active";
                case EmploymentStatus.Terminated:
                    return "Terminated";
                default:
                    return "";
            }
        }
    },
    actions: {
        addEmployees (apiResult: EmployeeListResult): void {
            for (const employee of apiResult.employees) {
                employee.dateOfBirth = moment(employee.dateOfBirth).format("MM/DD/YYYY");
                employee.terminationDate = employee.terminationDate ?
                    moment(employee.terminationDate).format("YYYY-MM-DD") : employee.terminationDate;
            }

            // A new record was added by another system user so we need to refresh all the stored pages.
            if (this.totalRecords !== apiResult.totalRecords) {
                this.pages = [];
            }

            const pages = [ ...this.pages ];
            pages[apiResult.pageNumber] = apiResult.employees;

            this.totalRecords = apiResult.totalRecords;
            this.pageNumber = apiResult.pageNumber;
            this.pages = pages;
        },
        addEmployee (employee: Employee): void {
            employee.dateOfBirth = moment(employee.dateOfBirth).format("MM/DD/YYYY");
            employee.ssn = employee.ssn.length > 4 ? employee.ssn.substr(employee.ssn.length - 4) : employee.ssn;
            employee.terminationDate = employee.terminationDate ?
                moment(employee.terminationDate).format("YYYY-MM-DD") : employee.terminationDate;

            if (!this.pages) {
                this.pages = [];
            }

            if (!this.pages.length) {
                this.pages.push([ employee ]);
            } else {
                const lastIndex = this.pages.length - 1;
                const lastPage = this.pages[lastIndex];
                const pages = [ ...this.pages ];

                if (lastPage.length < this.pageSize) {
                    pages[lastIndex].push(employee);
                } else {
                    pages.push([ employee ]);
                }

                this.pages = pages;
            }
        },
        clear () {
            const emptyState = generateEmptyState();
            for (const v of Object.keys(emptyState)) {
                this[v] = emptyState[v];
            }
        },
        clearEmployeePages () {
            this.pages = [];
            this.totalRecords = 0;
        },
        updateEmployee (employee: Employee): void {
            const currEmployee = findEmployee(this, employee.employeeId);
            employee.ssn = employee.ssn.length > 4 ? employee.ssn.substr(employee.ssn.length - 4)
                : employee.ssn;
            employee.dateOfBirth = moment(employee.dateOfBirth).format("MM/DD/YYYY");
            Object.assign(currEmployee, employee);
        },
        setGetFetchState (fetchState: FetchState) {
            this.getFetchState = fetchState;
        },
        setPageNumber (pageNumber: number) {
            this.pageNumber = pageNumber;
        },
        setSortOption (sortOption = EmployeeListSortOption.None, reset = false) {
            if (reset) {
                this.sortOrder = SortOrder.Ascending;
                this.sortOption = EmployeeListSortOption.Name;
            } else {
                let computedSortOrder = null;
                if (this.sortOption === sortOption) {
                    const invertedSortOrder = this.sortOrder === SortOrder.Descending ? SortOrder.Ascending
                        : SortOrder.Descending;
                    computedSortOrder = invertedSortOrder;
                } else {
                    computedSortOrder = SortOrder.Descending;
                }
                this.sortOrder = computedSortOrder;
                this.sortOption = sortOption;
            }

        },
        setSearchDropdownList (employees: EmployeeSearchDropdownItem[]) {
            this.searchDropdownList = [ ...employees ];
        },
        getEmployeesList (
            id: number, pageNumber: number, clearState: boolean, searchTerm: EmployeeListSearchFields) {

            return new Promise<void>((resolve, reject) => {

                if (clearState) {
                    this.clearEmployeePages();
                    UserService.getEmployeesList(id,
                            pageNumber,
                            this.pageSize,
                            this.sortOption,
                            this.sortOrder,
                            searchTerm)
                        .then(response => {
                            this.addEmployees(response);
                            this.setGetFetchState(FetchState.Loaded);
                            resolve();
                        }).catch(err => reject({ message: "User api returned non-OK status", payload: err }));
                }

                const pageObject = this.pages[pageNumber || 1];
                if (typeof pageObject !== "undefined") {
                    resolve();
                    return;
                } else {
                    UserService.getEmployeesList(id,
                            pageNumber,
                            this.pageSize,
                            this.sortOption,
                            this.sortOrder,
                            searchTerm)
                        .then(response => {
                            this.addEmployees(response);
                            this.setGetFetchState(FetchState.Loaded);
                            resolve();
                        }).catch(err => reject({ message: "User api returned non-OK status", payload: err }));
                }
            });
        },
        getEmployeeSearchList ({ employerId, searchTerm }): Promise<any> {
            return new Promise((resolve, reject) => {
                UserService.getEmployeeSearchList(employerId, searchTerm)
                    .then(response => {
                        this.setSearchDropdownList(response.data);
                    })
                    .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
            });
        },
        getEmployeeDetail ({ employerId, employeeId }): Promise<any> {
            return new Promise<void>((resolve, reject) => {
                if (employeeId === 0 || findEmployee(this, employeeId)) {
                    resolve();
                    return;
                }

                UserService.getEmployeeInformation(employerId, employeeId)
                    .then(response => {
                        if (!response) {
                            reject({ message: "There was a problem reaching our servers", payload: response });
                            return;
                        }
                        this.addEmployee(response.data);
                        resolve();
                    }).catch(err => reject({ message: "User api returned non-OK status", payload: err }));
            });
        },
        saveEmployeeInformation (request: Employee): Promise<any> {
            return new Promise((resolve, reject) => {
                UserService.saveEmployeeInformation(request)
                    .then(response => {
                        this.clear();
                        this.addEmployee(response.data);
                        resolve({ employeeId: response.data.employeeId });
                    })
                    .catch(err => reject({ message: "User api returned non-OK status", payload: err }));
            });
        },
        cancelEmployeePlanUpdate (request: Employee): Promise<any> {
            return new Promise((resolve, reject) => {
                UserService.cancelEmployeePlanUpdate(request)
                    .then(response => {
                        this.clear();
                        resolve({ employeeId: response.data.employeeId });
                    })
                    .catch(err => reject({ message: "User api returned non-OK status", payload: err }));
            });
        },
        resendEmployeeInviteLink (
            request: { employerId: number, employeeId: number })
            : Promise<any> {
            return new Promise<void>((resolve, reject) => {
                UserService.resendEmployeeInviteLink(request.employerId, request.employeeId).then(response => {
                        resolve();
                    })
                    .catch(err => reject({ message: "User api returned non-OK status", payload: err }));
            });
        },
        uploadCensusFile (request: FileUploadRequest)
            : Promise<any> {
            return new Promise<any>((resolve, reject) => {
                const userStore = useUserStore();
                UserService.postEmployerCensusFile(request.file, userStore.employerId)
                    .then(response => {
                        const notifierStore = useNotifierStore();
                        notifierStore.setInfo("Your file is processing, check the file status below.");

                        resolve(response);
                    })
                    .catch(err =>
                        storeHelper.handleError({ error: err, rethrow: true }));
            });
        }
    }
});